import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';

// App-level settings - used for web application UI and branding
export type AppSettings = {
    id: number | null;
    // Branding
    header_logo_image: string | null;
    app_name: string | null;
    // App UI Colors (used in web interface)
    primary_color: string; // Main brand color for headers, buttons, etc.
    secondary_color: string; // Secondary brand color for accents
    text_color: string; // Main text color for UI elements
    admin_accent_color: string; // Admin interface accent color
};

// Document-level settings - used only in generated PDF documents
export type DocumentSettings = {
    id: number | null;
    // Document images
    document_header_image: string | null; // NEW: Header image for PDF documents
    // Document text content
    header_text: string | null;
    footer_text: string | null;
    confidentiality_note_text: string | null;
    // Document Colors (used only in PDFs)
    document_primary_color: string; // Main color for document headers, titles
    document_secondary_color: string; // Secondary color for document elements
    document_text_color: string; // Main text color in documents
    // Detailed PDF document colors
    header_text_color: string;
    footer_text_color: string;
    confidentiality_note_color: string;
    document_param_text_color: string;
    document_release_text_color: string;
    general_title_color: string;
    toc_chapter_title_color: string;
    toc_section_title_color: string;
    chapter_cover_page_color: string;
    vendor_email_color: string;
    vendor_section_color: string;
};

export const appSettingsApi = createApi({
    reducerPath: 'app-settings-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['AppSettings'],
    endpoints: (builder) => ({
        getAppSettings: builder.query<AppSettings, void>({
            query: () => ({
                url: '/app-settings/',
                method: 'GET',
            }),
            providesTags: ['AppSettings'],
        }),
    }),
});

export const {
    useGetAppSettingsQuery,
} = appSettingsApi;
